import { createContext, useContext, useMemo } from 'react';
import type { CSSProperties, PropsWithChildren } from 'react';
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface Props {
  id: UniqueIdentifier;
  style?: CSSProperties;
  disabled?: boolean;
}

interface Context {
  attributes: DraggableAttributes | Record<string, never>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableGridItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

export function SortableGridItem({
  children,
  id,
  style: itemStyle,
  disabled,
}: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled });

  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  );

  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
    boxSizing: 'border-box',
    position: 'relative',
    ...itemStyle,
  };

  return (
    <SortableGridItemContext.Provider value={context}>
      <Box ref={setNodeRef} style={style}>
        {children}
      </Box>
    </SortableGridItemContext.Provider>
  );
}

export function GridDragHandle({
  corner = 'right',
  sx,
  iconSx,
}: {
  corner?: 'left' | 'right';
  sx?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
}) {
  const { attributes, listeners, ref } = useContext(SortableGridItemContext);

  return (
    <IconButton
      ref={ref}
      size="small"
      sx={[
        {
          position: 'absolute',
          top: 8,
          left: corner === 'left' ? 8 : undefined,
          right: corner === 'right' ? 8 : undefined,
          zIndex: 2,
          backgroundColor: 'rgba(255,255,255,0.95)',
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'move',
          touchAction: 'none',
          '&:hover': { backgroundColor: 'white' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...attributes}
      {...(listeners ?? {})}
      aria-label="Drag to reorder">
      <DragIndicatorIcon fontSize="small" sx={iconSx} />
    </IconButton>
  );
}
